// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// ** Axios Imports
import axios from 'axios';

// ** Get HRMS users
export const getHRMSUsers = createAsyncThunk(
  'userCustomerAccess/getHRMSUsers',
  async () => {
    const response = await axios.get(`/v1/users`);
    return response.data;
  }
);

// ** Get all user-customer access records
export const getAllUserAccess = createAsyncThunk(
  'userCustomerAccess/getAllUserAccess',
  async (params = {}) => {
    const response = await axios.get(`/v1/user-customer-access`, { params });
    return response.data;
  }
);

// ** Get user access by userId
export const getUserAccess = createAsyncThunk(
  'userCustomerAccess/getUserAccess',
  async (userId) => {
    const response = await axios.get(`/v1/user-customer-access/${userId}`);
    return response.data;
  }
);

// ** Update user access configuration
export const updateUserAccess = createAsyncThunk(
  'userCustomerAccess/updateUserAccess',
  async ({ userId, accessConfig }) => {
    const response = await axios.put(
      `/v1/user-customer-access/${userId}`,
      accessConfig
    );
    return { userId, data: response.data };
  }
);

// ** Grant all-customer access
export const grantAllCustomersAccess = createAsyncThunk(
  'userCustomerAccess/grantAllCustomersAccess',
  async (userData) => {
    const response = await axios.post(
      `/v1/user-customer-access/grant-all`,
      userData
    );
    return response.data;
  }
);

// ** Grant specific customer access
export const grantCustomerAccess = createAsyncThunk(
  'userCustomerAccess/grantCustomerAccess',
  async (userData) => {
    const response = await axios.post(`/v1/user-customer-access/grant`, userData);
    return response.data;
  }
);

// ** Revoke customer access
export const revokeCustomerAccess = createAsyncThunk(
  'userCustomerAccess/revokeCustomerAccess',
  async (userData) => {
    const response = await axios.post(
      `/v1/user-customer-access/revoke`,
      userData
    );
    return response.data;
  }
);

// ** Revoke all access
export const revokeAllAccess = createAsyncThunk(
  'userCustomerAccess/revokeAllAccess',
  async (userData) => {
    const response = await axios.post(
      `/v1/user-customer-access/revoke-all`,
      userData
    );
    return response.data;
  }
);

// ** Get available spend categories
export const getAvailableCategories = createAsyncThunk(
  'userCustomerAccess/getAvailableCategories',
  async () => {
    const response = await axios.get(`/v1/spend-categories/available`);
    return response.data.categories;
  }
);

// ** Get user category assignments
export const getUserCategoryAssignments = createAsyncThunk(
  'userCustomerAccess/getUserCategoryAssignments',
  async (userId) => {
    const response = await axios.get(
      `/v1/users/${userId}/category-assignments`
    );
    return response.data.assignments;
  }
);

// ** Add category assignments
export const addCategoryAssignments = createAsyncThunk(
  'userCustomerAccess/addCategoryAssignments',
  async ({ userId, categoryNames }) => {
    const response = await axios.post(
      `/v1/users/${userId}/category-assignments`,
      { categoryNames }
    );
    return response.data.assignments;
  }
);

// ** Remove category assignment
export const removeCategoryAssignment = createAsyncThunk(
  'userCustomerAccess/removeCategoryAssignment',
  async ({ userId, categoryName }) => {
    await axios.delete(
      `/v1/users/${userId}/category-assignments/${encodeURIComponent(
        categoryName
      )}`
    );
    return categoryName;
  }
);

// Entra oids come back in whichever case SQL Server stored them; compare loosely.
const sameId = (a, b) => {
  return typeof a === 'string' && typeof b === 'string' && a.toLowerCase() === b.toLowerCase();
};

// Replace the row for that oid, or add it. Rows are the API's raw FmsUsers
// shape (userId, email, displayName, isActive, grantedAt, deactivatedAt, …).
const upsertFmsUser = (rows, row) => {
  if (!row || !row.userId) return rows;
  const idx = rows.findIndex((r) => sameId(r.userId, row.userId));
  if (idx === -1) return [...rows, row];
  return rows.map((r, i) => (i === idx ? { ...r, ...row } : r));
};

// Reflect an approve/revoke result in the open per-user device list.
const replaceUserDevice = (userDevices, row) => {
  if (!userDevices || !row?.id) return userDevices;
  return {
    ...userDevices,
    devices: userDevices.devices.map((d) => (d.id === row.id ? { ...d, ...row } : d))
  };
};

// ---------------------------------------------------------------------------
// FMS admission (FmsUsers) + device approval queue — /v1/fms-users/*
// Both behind roles Admin / UserAccess.ReadWrite.All, same as this page.
// ---------------------------------------------------------------------------

// ** Every FmsUsers row, active or not
export const getFmsUsers = createAsyncThunk(
  'userCustomerAccess/getFmsUsers',
  async () => {
    const response = await axios.get(`/v1/fms-users`);
    return response.data;
  }
);

// ** Grant FMS access (a deactivated user is reactivated by the API)
export const grantFmsAccess = createAsyncThunk(
  'userCustomerAccess/grantFmsAccess',
  async ({ userId, email, displayName }) => {
    const response = await axios.post(
      `/v1/fms-users/${userId}/grant`,
      { email, displayName },
      { skipGlobalErrorHandler: true }
    );
    return response.data;
  }
);

// ** Deactivate: IsActive = 0, every device revoked. Self-target → 400.
export const deactivateFmsUser = createAsyncThunk(
  'userCustomerAccess/deactivateFmsUser',
  async ({ userId, reason }) => {
    const response = await axios.post(
      `/v1/fms-users/${userId}/deactivate`,
      { reason: reason || null },
      { skipGlobalErrorHandler: true }
    );
    return response.data;
  }
);

// ** Reactivate (no body: the API's validator declares none)
export const reactivateFmsUser = createAsyncThunk(
  'userCustomerAccess/reactivateFmsUser',
  async ({ userId }) => {
    const response = await axios.post(
      `/v1/fms-users/${userId}/reactivate`,
      {},
      { skipGlobalErrorHandler: true }
    );
    return response.data;
  }
);

// ** Receives escalations (FR-25): one flag on the admission record, Admin only,
// plus its optional scope ('All' | 'Customers'); an omitted scope is kept.
// PATCH is the canonical verb; the API accepts PUT too.
export const setReceivesEscalations = createAsyncThunk(
  'userCustomerAccess/setReceivesEscalations',
  async ({ userId, on, scope }) => {
    const response = await axios.patch(
      `/v1/fms-users/${userId}/receives-escalations`,
      scope ? { on: !!on, scope } : { on: !!on },
      { skipGlobalErrorHandler: true }
    );
    return response.data;
  }
);

// ** Every device of one user (any status), newest first — the admin Revoke list
export const getUserDevices = createAsyncThunk(
  'userCustomerAccess/getUserDevices',
  async (userId) => {
    const response = await axios.get(`/v1/fms-users/${userId}/devices`);
    return { userId, devices: response.data };
  }
);

// ** Devices waiting for approval, oldest first
export const getPendingDevices = createAsyncThunk(
  'userCustomerAccess/getPendingDevices',
  async () => {
    const response = await axios.get(`/v1/fms-users/devices/pending`);
    return response.data;
  }
);

export const approveDevice = createAsyncThunk(
  'userCustomerAccess/approveDevice',
  async ({ id }) => {
    const response = await axios.post(
      `/v1/fms-users/devices/${id}/approve`,
      {},
      { skipGlobalErrorHandler: true }
    );
    return response.data;
  }
);

// ** Reject a pending device or revoke an active one — the API has one verb
export const revokeDevice = createAsyncThunk(
  'userCustomerAccess/revokeDevice',
  async ({ id, reason }) => {
    const response = await axios.post(
      `/v1/fms-users/devices/${id}/revoke`,
      { reason: reason || null },
      { skipGlobalErrorHandler: true }
    );
    return response.data;
  }
);

export const userCustomerAccessSlice = createSlice({
  name: 'userCustomerAccess',
  initialState: {
    data: [],
    hrmsUsers: [],
    selectedUserAccess: null,
    availableCategories: [],
    userCategoryAssignments: [],
    isLoading: false,
    isLoadingUsers: false,
    isUpdating: false,
    isCategoriesLoading: false,
    isAssignmentsLoading: false,
    fmsUsers: [],
    isLoadingFmsUsers: false,
    pendingDevices: [],
    isLoadingDevices: false,
    // The user whose devices modal is open: { userId, devices }
    userDevices: null,
    isLoadingUserDevices: false,
    userDevicesError: null,
    isUpdatingFms: false,
    error: null,
    successMessage: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    setSelectedUserAccess: (state, action) => {
      state.selectedUserAccess = action.payload;
    },
    clearUserCategoryAssignments: (state) => {
      state.userCategoryAssignments = [];
    },
    clearUserDevices: (state) => {
      state.userDevices = null;
      state.userDevicesError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get HRMS users
      .addCase(getHRMSUsers.pending, (state) => {
        state.isLoadingUsers = true;
      })
      .addCase(getHRMSUsers.fulfilled, (state, action) => {
        state.hrmsUsers = action.payload;
        state.isLoadingUsers = false;
      })
      .addCase(getHRMSUsers.rejected, (state) => {
        state.isLoadingUsers = false;
      })
      // Get all user access
      .addCase(getAllUserAccess.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllUserAccess.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getAllUserAccess.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // Get user access
      .addCase(getUserAccess.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserAccess.fulfilled, (state, action) => {
        state.selectedUserAccess = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getUserAccess.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // Update user access
      .addCase(updateUserAccess.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateUserAccess.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.error = null;
        state.successMessage = 'User access updated successfully';
        // Refresh the data
        state.selectedUserAccess = null;
      })
      .addCase(updateUserAccess.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.error.message;
      })
      // Grant all customers access
      .addCase(grantAllCustomersAccess.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(grantAllCustomersAccess.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.error = null;
        state.successMessage = 'All-customer access granted successfully';
      })
      .addCase(grantAllCustomersAccess.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.error.message;
      })
      // Grant customer access
      .addCase(grantCustomerAccess.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(grantCustomerAccess.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.error = null;
        state.successMessage = 'Customer access granted successfully';
      })
      .addCase(grantCustomerAccess.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.error.message;
      })
      // Revoke customer access
      .addCase(revokeCustomerAccess.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(revokeCustomerAccess.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.error = null;
        state.successMessage = 'Customer access revoked successfully';
      })
      .addCase(revokeCustomerAccess.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.error.message;
      })
      // Revoke all access
      .addCase(revokeAllAccess.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(revokeAllAccess.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.error = null;
        state.successMessage = 'All access revoked successfully';
      })
      .addCase(revokeAllAccess.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.error.message;
      })
      // Get available categories
      .addCase(getAvailableCategories.pending, (state) => {
        state.isCategoriesLoading = true;
      })
      .addCase(getAvailableCategories.fulfilled, (state, action) => {
        state.availableCategories = action.payload;
        state.isCategoriesLoading = false;
      })
      .addCase(getAvailableCategories.rejected, (state) => {
        state.isCategoriesLoading = false;
      })
      // Get user category assignments
      .addCase(getUserCategoryAssignments.pending, (state) => {
        state.isAssignmentsLoading = true;
      })
      .addCase(getUserCategoryAssignments.fulfilled, (state, action) => {
        state.userCategoryAssignments = action.payload;
        state.isAssignmentsLoading = false;
      })
      .addCase(getUserCategoryAssignments.rejected, (state) => {
        state.isAssignmentsLoading = false;
      })
      // Add category assignments
      .addCase(addCategoryAssignments.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(addCategoryAssignments.fulfilled, (state, action) => {
        state.userCategoryAssignments = action.payload;
        state.isUpdating = false;
      })
      .addCase(addCategoryAssignments.rejected, (state) => {
        state.isUpdating = false;
      })
      // Remove category assignment
      .addCase(removeCategoryAssignment.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(removeCategoryAssignment.fulfilled, (state, action) => {
        state.userCategoryAssignments = state.userCategoryAssignments.filter(
          (cat) => cat !== action.payload
        );
        state.isUpdating = false;
      })
      .addCase(removeCategoryAssignment.rejected, (state) => {
        state.isUpdating = false;
      })
      // FMS admission
      .addCase(getFmsUsers.pending, (state) => {
        state.isLoadingFmsUsers = true;
      })
      .addCase(getFmsUsers.fulfilled, (state, action) => {
        state.fmsUsers = action.payload;
        state.isLoadingFmsUsers = false;
      })
      .addCase(getFmsUsers.rejected, (state) => {
        state.isLoadingFmsUsers = false;
      })
      .addCase(grantFmsAccess.pending, (state) => {
        state.isUpdatingFms = true;
      })
      .addCase(grantFmsAccess.fulfilled, (state, action) => {
        state.isUpdatingFms = false;
        state.fmsUsers = upsertFmsUser(state.fmsUsers, action.payload);
      })
      .addCase(grantFmsAccess.rejected, (state) => {
        state.isUpdatingFms = false;
      })
      .addCase(deactivateFmsUser.pending, (state) => {
        state.isUpdatingFms = true;
      })
      .addCase(deactivateFmsUser.fulfilled, (state, action) => {
        state.isUpdatingFms = false;
        state.fmsUsers = upsertFmsUser(state.fmsUsers, action.payload);
        // Every device of that user is now revoked, pending ones included.
        state.pendingDevices = state.pendingDevices.filter(
          (d) => !sameId(d.userId, action.payload?.userId)
        );
        if (state.userDevices && sameId(state.userDevices.userId, action.payload?.userId)) {
          // Stale until re-fetched; the modal reloads on next open.
          state.userDevices = null;
        }
      })
      .addCase(deactivateFmsUser.rejected, (state) => {
        state.isUpdatingFms = false;
      })
      .addCase(reactivateFmsUser.pending, (state) => {
        state.isUpdatingFms = true;
      })
      .addCase(reactivateFmsUser.fulfilled, (state, action) => {
        state.isUpdatingFms = false;
        state.fmsUsers = upsertFmsUser(state.fmsUsers, action.payload);
      })
      .addCase(reactivateFmsUser.rejected, (state) => {
        state.isUpdatingFms = false;
      })
      .addCase(setReceivesEscalations.fulfilled, (state, action) => {
        state.fmsUsers = upsertFmsUser(state.fmsUsers, action.payload);
      })
      // Device approval queue
      .addCase(getPendingDevices.pending, (state) => {
        state.isLoadingDevices = true;
      })
      .addCase(getPendingDevices.fulfilled, (state, action) => {
        state.pendingDevices = action.payload;
        state.isLoadingDevices = false;
      })
      .addCase(getPendingDevices.rejected, (state) => {
        state.isLoadingDevices = false;
      })
      .addCase(approveDevice.pending, (state) => {
        state.isUpdatingFms = true;
      })
      .addCase(approveDevice.fulfilled, (state, action) => {
        state.isUpdatingFms = false;
        state.pendingDevices = state.pendingDevices.filter((d) => d.id !== action.payload?.id);
        state.userDevices = replaceUserDevice(state.userDevices, action.payload);
      })
      .addCase(approveDevice.rejected, (state) => {
        state.isUpdatingFms = false;
      })
      .addCase(revokeDevice.pending, (state) => {
        state.isUpdatingFms = true;
      })
      .addCase(revokeDevice.fulfilled, (state, action) => {
        state.isUpdatingFms = false;
        state.pendingDevices = state.pendingDevices.filter((d) => d.id !== action.payload?.id);
        state.userDevices = replaceUserDevice(state.userDevices, action.payload);
      })
      // Per-user device list
      .addCase(getUserDevices.pending, (state) => {
        state.isLoadingUserDevices = true;
        state.userDevicesError = null;
      })
      .addCase(getUserDevices.fulfilled, (state, action) => {
        state.userDevices = action.payload;
        state.isLoadingUserDevices = false;
      })
      .addCase(getUserDevices.rejected, (state, action) => {
        state.isLoadingUserDevices = false;
        state.userDevicesError = action.error?.message || 'Failed to load devices';
      })
      .addCase(revokeDevice.rejected, (state) => {
        state.isUpdatingFms = false;
      });
  }
});

export const {
  clearError,
  clearSuccessMessage,
  setSelectedUserAccess,
  clearUserCategoryAssignments,
  clearUserDevices
} = userCustomerAccessSlice.actions;

export default userCustomerAccessSlice.reducer;
